import React, { useEffect } from 'react';
import CustomDropdown from '../../../../base/custom-dropdown';
import RadioButton from '../../../../base/radio';
import AppButton from '../../../../base/button';
import { useFormik } from 'formik';
import { useBeneficiaryStore } from '../../../../../store';
import MoneyIncome from '../../../../../assets/components/money-income';
import beneficiarySchemas from '../../../../../constants/schemas/beneficiary';
import { PaymentMethodEnum } from '../../../../../models/beneficiary.model';
import usePayoutStore from '../../../../../store/payout';

interface LocationAndTransactionTypeProps {
  goNext: () => void;
  caller?: string;
}

const LocationAndTransactionType: React.FC<LocationAndTransactionTypeProps> = ({
  goNext,
  caller,
}) => {
  const { setBeneficiaryPayload, beneficiaryPayload } = useBeneficiaryStore();
  const { payoutPayload } = usePayoutStore();

  const formik = useFormik({
    initialValues: {
      currencyCode: beneficiaryPayload.currencyCode,
      transactionType: beneficiaryPayload.transactionType || 'local',
      paymentMethod: beneficiaryPayload.paymentMethod,
    },
    validationSchema: beneficiarySchemas[0],
    validateOnMount: true,

    async onSubmit(values) {
      setBeneficiaryPayload({
        ...beneficiaryPayload,
        ...values,
        paymentType:
          PaymentMethodEnum[
            values.paymentMethod as keyof typeof PaymentMethodEnum
          ],
      });
      goNext();
    },
  });

  useEffect(() => {
    setBeneficiaryPayload({
      ...beneficiaryPayload,
      transactionType: formik.values.transactionType,
      paymentMethod: formik.values.paymentMethod,
      paymentType:
        PaymentMethodEnum[
          formik.values.paymentMethod as keyof typeof PaymentMethodEnum
        ],
      currencyCode: formik.values.currencyCode,
    });
  }, [
    formik.values.transactionType,
    formik.values.paymentMethod,
    formik.values.currencyCode,
  ]);

  const handleCurrencySelect = (option: string) => {
    formik.setFieldValue('currencyCode', option);
  };

  const handlePaymentMethodSelect = (option: string) => {
    formik.setFieldValue('paymentMethod', option);
  };

  const handleTransactionTypeChange = (value: string) => {
    formik.setFieldValue('transactionType', value);
  };

  const transactionTypeOptions = [
    { value: 'local', label: 'Local' },
    { value: 'international', label: 'International' },
  ];

  const availablePaymentMethods = () => {
    if (
      (formik.values.currencyCode === 'USD' ||
        payoutPayload.toCurrencyCode === 'USD') &&
      formik.values.transactionType === 'local'
    ) {
      return [{ name: 'USDLocal', label: 'USDLocal' }];
    }

    if (
      ((formik.values.currencyCode === 'USD' ||
        payoutPayload.toCurrencyCode === 'USD') &&
        formik.values.transactionType === 'international') ||
      formik.values.currencyCode === 'EUR'
    ) {
      return [
        { name: 'SWIFT', label: 'SWIFT' },
        { name: 'SWIFTUnknown', label: 'SWIFTUnknown' },
        { name: 'IBAN', label: 'IBAN' },
      ];
    }

    return [];
  };

  return (
    <div>
      <form
        className="flex flex-col gap-8"
        onSubmit={formik.handleSubmit}
        action="#"
      >
        {caller !== 'payout' && (
          <p className="text-lg font-semibold">Location & Transaction Type</p>
        )}
        {caller !== 'payout' && (
          <CustomDropdown
            icon={<MoneyIncome />}
            label={'Currency'}
            options={['USD', 'EUR'].map((currency) => ({
              name: currency,
              label: currency,
            }))}
            selectedValue={
              formik.values.currencyCode
                ? formik.values.currencyCode
                : (beneficiaryPayload?.currencyCode ?? '')
            }
            onSelectChange={handleCurrencySelect}
          />
        )}

        <RadioButton
          title="Transaction Type"
          name="transactionType"
          options={transactionTypeOptions}
          selectedValue={
            formik.values.transactionType
              ? formik.values.transactionType
              : (beneficiaryPayload?.transactionType ?? '')
          }
          onChange={handleTransactionTypeChange}
        />

        <div className={caller === 'payout' ? 'mb-6 w-1/3' : ''}>
          <CustomDropdown
            label={'Payment Method'}
            options={availablePaymentMethods()}
            selectedValue={
              formik.values.paymentMethod
                ? formik.values.paymentMethod
                : (beneficiaryPayload?.paymentMethod ?? '')
            }
            onSelectChange={handlePaymentMethodSelect}
          />
        </div>

        {caller !== 'payout' && (
          <div className="flex gap-4 mt-8">
            <AppButton
              label="Next"
              type="primary"
              submitType="submit"
              disabled={!formik.isValid}
            />
          </div>
        )}
      </form>
    </div>
  );
};

export default LocationAndTransactionType;
